import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { type AudienceOptionType } from '@/api/audience';
import { type ChannelWeeklyRowDataType, useChannelWeeklyVisits } from '@/api/channel';
import Flare, { Axis, type FlareSeriesOptions, Legend, Notes, Tooltip } from '@/components/Flare';
import Line from '@/components/Flare/marks/Line';
import { usePageFilterContext } from '@/components/page';
import { NotesPanel } from '@/components/panels';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { getVendorColor } from '@/constants/colors/vendor';
import { MonthDateShortFormat, MonthDateYearFormat } from '@/constants/formats';
import { getUTCTime } from '@/helper/dateHelper';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  channelId?: string;
};

enum CHART_MODE {
  allVisits,
  targetVisits,
  costPerVisit,
  costPerTargetVisit,
}

const getAmountByChartMode = (
  chartMode: CHART_MODE,
  object: ChannelWeeklyRowDataType,
  audience?: AudienceOptionType,
) => {
  switch (chartMode) {
    case CHART_MODE.costPerTargetVisit:
      return audience?.isTAM ? object.spendPerVisit.tam : object.spendPerVisit.audience;
    case CHART_MODE.targetVisits:
      return audience?.isTAM ? object.visits.tam : object.visits.audience;
    case CHART_MODE.costPerVisit:
      return object.spendPerVisit.all;
    case CHART_MODE.allVisits:
    default:
      return object.visits.all;
  }
};

const getChartModeOptions = (audience?: AudienceOptionType) => {
  const defaultOptions = [
    { value: CHART_MODE.allVisits, label: t`Visits` },
    {
      value: CHART_MODE.targetVisits,
      label: audience?.isTAM
        ? t`TAM Visits`
        : t`${audience?.name ? audience.name : t`Audience`} Visits`,
    },
    { value: CHART_MODE.costPerVisit, label: t`$/Visit` },
    {
      value: CHART_MODE.costPerTargetVisit,
      label: audience?.isTAM
        ? t`TAM $/Visit`
        : t`${audience?.name ? audience.name : t`Audience`} $/Visit`,
    },
  ];
  return defaultOptions;
};

const ChannelWeeklyVisitsChart = ({ channelId }: Props) => {
  const { pageFilters } = usePageFilterContext();
  const { startDate, endDate, audience } = pageFilters;
  const [chartMode, setChartMode] = useState<CHART_MODE>(CHART_MODE.allVisits);
  const { channelWeeklyVisits, isLoading, error } = useChannelWeeklyVisits(
    channelId,
    startDate,
    endDate,
    audience?.id,
  );

  const sanitizedData: FlareSeriesOptions[] | undefined = useMemo(() => {
    if (channelWeeklyVisits == null) {
      return undefined;
    }

    return channelWeeklyVisits.map((visitsByVendor) => ({
      id: visitsByVendor.vendor.id,
      name: visitsByVendor.vendor.name,
      data: visitsByVendor.data.map((d) => ({
        date: d.sundayOfWeek,
        amount: getAmountByChartMode(chartMode, d, audience),
      })),
    }));
  }, [channelWeeklyVisits, chartMode, audience]);

  return (
    <NotesPanel
      startDate={startDate}
      endDate={endDate}
      title={<Trans>Total Visits</Trans>}
      actions={
        <Tabs value={chartMode} onValueChange={(value) => setChartMode(value as CHART_MODE)}>
          <TabsList>
            {getChartModeOptions(audience).map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      }
      noPadding
      style={{ height: 565 }}
      verifyC99Tag
    >
      <Flare
        data={sanitizedData}
        width="auto"
        height="auto"
        parseX={getUTCTime}
        colors={channelWeeklyVisits?.map((visitsByVendor) => getVendorColor(visitsByVendor.vendor))}
        isLoading={isLoading}
        error={error}
      >
        <Legend />
        <Line x="date" y="amount" />
        <Axis
          position="left"
          labelFormat={(item) => numberFormat(item.value, { compact: true })}
          title={t`Visits`}
        />
        <Axis
          type="datetime"
          crosshair="line"
          position="bottom"
          labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
        />
        <Tooltip
          titleFormat={(point) => dayjs(point?.x).format(MonthDateYearFormat)}
          rowValueFormat={(point) => numberFormat(point?.y)}
          showTotalRow={sanitizedData && sanitizedData.length > 1}
          shared
        />
        <Notes />
      </Flare>
    </NotesPanel>
  );
};

export default ChannelWeeklyVisitsChart;
